import { Fragment, useState } from 'react';

import { gql, useMutation } from '@apollo/client';
import { AlertTitle } from '@material-ui/lab';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCard,
  DevHubGrid,
  DevHubModal,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';

const REMOVE_CLOUD_ACCOUNT_MUTATION = gql`
  mutation DeleteCloudAccountFromApp(
    $input: RemoveApplicationConnectionInput!
  ) {
    devhub {
      connections {
        cloudAccount {
          removeApplicationConnection(input: $input) {
            message
            success
          }
        }
      }
    }
  }
`;

const CREATE_CLOUD_ACCOUNT_MUTATION = gql`
  mutation EstablishCloudAppConnection(
    $input: CreateApplicationConnectionInput!
  ) {
    devhub {
      connections {
        cloudAccount {
          addApplicationConnection(input: $input) {
            message
            success
          }
        }
      }
    }
  }
`;

/**
 * This component is used to manually create a new DevHub Extension
 * @constructor
 */
export const CloudAccountManager = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    applicationId: '',
    cloudAccountId: '',
  });

  const [createCloudAccount] = useMutation(CREATE_CLOUD_ACCOUNT_MUTATION, {
    onCompleted: data => {
      if (
        data?.devhub?.connections?.cloudAccount?.addApplicationConnection
          ?.success
      ) {
        setSuccessMessage('Cloud account added successfully!');
        setSuccessOpen(true);
      } else {
        setErrorMessage(
          `Failed to add cloud account. ${data?.devhub?.connections?.cloudAccount?.addApplicationConnection?.message} `,
        );
        setErrorOpen(true);
      }
    },
  });

  const [removeCloudAccount] = useMutation(REMOVE_CLOUD_ACCOUNT_MUTATION, {
    onCompleted: data => {
      if (
        data?.devhub?.connections?.cloudAccount?.removeApplicationConnection
          ?.success
      ) {
        setSuccessMessage('Cloud account removed successfully!');
        setSuccessOpen(true);
      } else {
        setErrorMessage('Failed to remove cloud account');
        setErrorOpen(true);
      }
    },
    onError: error => {
      const graphQLError = error.graphQLErrors[0];
      if (graphQLError?.extensions?.code === 'LAST_APPLICATION_CONNECTION') {
        setErrorOpen(true);
        setErrorMessage(
          'Failed to remove cloud account: This cloud account only has one application connection left.',
        );
      } else {
        setErrorOpen(true);
        setErrorMessage(
          `Failed to remove cloud account. ${graphQLError.message}`,
        );
      }
    },
  });

  const handleCreate = async () => {
    const inputData = {
      applicationId: Number(formData.applicationId),
      cloudAccountIds: formData.cloudAccountId,
    };

    await createCloudAccount({ variables: { input: inputData } });
  };

  const handleRemove = async () => {
    const inputData = {
      applicationId: Number(formData.applicationId),
      cloudAccountId: formData.cloudAccountId,
    };

    await removeCloudAccount({ variables: { input: inputData } });
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prevData => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleReset = () => {
    setFormData({
      applicationId: '',
      cloudAccountId: '',
    });
    setSuccessOpen(false);
    setErrorOpen(false);
  };

  const handleOpen = () => {
    setModalIsOpen(true);
  };

  const handleClose = () => {
    setModalIsOpen(false);
    handleReset();
  };

  // @ts-ignore
  return (
    <Fragment>
      <DevHubCard
        density="compact"
        title="Cloud Account Manager"
        actionItem={
          <DevHubGrid container margin="xsmall">
            <DevHubButton
              buttonColor="primary"
              variant="outlined"
              onClick={handleOpen}
            >
              Open
            </DevHubButton>
          </DevHubGrid>
        }
        subTitle="Manually create an extension"
      />
      <DevHubModal open={modalIsOpen} onClose={handleClose}>
        <Fragment>
          <DevHubTypography variant="h3">
            Cloud Account Manager
          </DevHubTypography>

          <DevHubTextField
            label="Application ID"
            value={formData.applicationId}
            onChange={(e: { target: { value: string } }) =>
              handleChange('applicationId', e.target.value)
            }
            fullWidth
            helperText="The Application ID"
          />
          <DevHubTextField
            label="Cloud Account ID"
            value={formData.cloudAccountId}
            onChange={(e: { target: { value: string } }) =>
              handleChange('cloudAccountId', e.target.value)
            }
            fullWidth
            helperText="The Cloud Account ID"
          />
          <DevHubBox display="flex" justifyContent="space-between">
            <DevHubButton
              textColor="white"
              buttonColor="green"
              onClick={handleCreate}
            >
              {' '}
              Create{' '}
            </DevHubButton>

            <DevHubButton
              textColor="white"
              buttonColor="red"
              onClick={handleRemove}
            >
              {' '}
              Remove{' '}
            </DevHubButton>
          </DevHubBox>
          <DevHubBox mt={2}>
            {successOpen && (
              <DevHubAlert
                onClose={() => setSuccessOpen(false)}
                severity="success"
                variant="filled"
              >
                <AlertTitle>Success</AlertTitle>
                {successMessage}
              </DevHubAlert>
            )}
            {errorOpen && (
              <DevHubAlert
                onClose={() => setErrorOpen(false)}
                severity="error"
                variant="filled"
              >
                <AlertTitle>Error</AlertTitle>
                {errorMessage}
              </DevHubAlert>
            )}
          </DevHubBox>
        </Fragment>
      </DevHubModal>
    </Fragment>
  );
};
